import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, TextField, Button, IconButton, Grid, MenuItem, Switch, FormControlLabel,Card } from "@mui/material";
import { toast } from "react-hot-toast";
import Autocomplete from "@mui/material/Autocomplete";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllSeasonsAction,
  getAllZonesAction,
  getAllRegionsAction,
  getAllAreasAction,
  getAllDistrictsAction,
  getAllMandalsAction,
  createPreSeasonActivityAction,
  clearCreatePreSeasonActivityAction,
  updatePreSeasonActivityAction,
  clearUpdatePreSeasonActivityAction,
  getPreSeasonActivityByIdAction,
} from "../../../store/actions";

// Utility functions outside component to avoid re-creation
const normalizeTargetType = (type) => String(type || "").toLowerCase();

const getUiTargetType = (type) => {
  const key = normalizeTargetType(type);
  if (key === "district") return "District";
  if (key === "mandal") return "Mandal";
  if (key === "zone") return "Zone";
  if (key === "region") return "Region";
  if (key === "area") return "Area";
  return "";
};

// No longer accepts editData prop; uses Redux state instead
export default function PreSeasonAMRMInformation() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const id = location?.state?.activityId;
  const isEditMode = Boolean(id);

  // Redux selectors

  const { successData: seasons } = useSelector((state) => state.getAllSeasonsReducer);
  const seasonList = useMemo(() => seasons?.data?.seasons || [], [seasons]);

  const { successData: zones, loading: zonesLoading } = useSelector((state) => state.getAllZonesReducer);
  const zoneList = useMemo(() => zones?.data?.zones || [], [zones]);

  const { successData: regions, loading: regionsLoading } = useSelector((state) => state.getAllRegionsReducer);
  const regionList = useMemo(() => regions?.data?.regions || [], [regions]);

  const { successData: areas, loading: areasLoading } = useSelector((state) => state.getAllAreasReducer);
  const areaList = useMemo(() => areas?.data?.areas || [], [areas]);

  const { successData: districts, loading: districtsLoading } = useSelector((state) => state.getAllDistrictsReducer);
  const districtList = useMemo(() => districts?.data?.districts || [], [districts]);

  const { successData: mandals, loading: mandalsLoading } = useSelector((state) => state.getAllMandalsReducer);
  const mandalList = useMemo(() => mandals?.data?.mandals || [], [mandals]);

  const { successData: createPreSeasonSuccess ,error:createPreSeasonError,errorInfo:createPreSeasonErrorInfo} = useSelector((state) => state.createPreSeasonActivityReducer);
  const { successData: updatePreSeasonSuccess ,error:updatePreSeasonError} = useSelector((state) => state.updatePreSeasonActivityReducer);
  const { successData: preSeasonActivityById } = useSelector((state) => state.getPreSeasonActivityByIdReducer);
  

  useEffect(() => {
    dispatch(getAllSeasonsAction());
    dispatch(getAllZonesAction());
    dispatch(getAllRegionsAction());
    dispatch(getAllAreasAction());
    dispatch(getAllDistrictsAction());
    dispatch(getAllMandalsAction());

  }, [dispatch]);
  
  useEffect(() => {
    if (id) {
      dispatch(getPreSeasonActivityByIdAction({
        endPoint : `target/pre-season/${id}`,
      }));
    }
  }, [dispatch, id]);

  // Extract edit data from Redux state
  const editData = preSeasonActivityById?.data || null;
  
  // Toast and navigation for create
  useEffect(() => {
    if (createPreSeasonSuccess) {
      toast.success("Pre season created successfully");
      dispatch(clearCreatePreSeasonActivityAction());
      navigate(-1);
    } else if (createPreSeasonError) {
      const errMsg = createPreSeasonErrorInfo?.message || createPreSeasonErrorInfo || "Failed to create pre activity";
      toast.error(errMsg);
      dispatch(clearCreatePreSeasonActivityAction());
    }
  }, [createPreSeasonSuccess, createPreSeasonError, createPreSeasonErrorInfo, dispatch, navigate]);

  // Toast and navigation for update
  useEffect(() => {
    if (updatePreSeasonSuccess) {
      toast.success("Pre activity updated successfully");
      dispatch(clearUpdatePreSeasonActivityAction());
      navigate(-1);
    } else if (updatePreSeasonError) {
      toast.error("Failed to update pre activity");
      dispatch(clearUpdatePreSeasonActivityAction());
    }
  }, [updatePreSeasonSuccess, updatePreSeasonError, dispatch, navigate]);

  // Visibility mapping for location fields
  const fieldsByType = {
    zone: ["zone"],
    region: ["zone", "region"],
    area: ["zone", "region", "area"],
    district: ["zone", "region", "area", "district"],
    mandal: ["zone", "region", "area", "district", "mandal"],
  };

  
  // Initial values for create or update - memoized to prevent unnecessary resets
  const initialValues = useMemo(() => {
    if (editData) {
      // ...existing code...
      const populateHierarchy = () => {
        const hierarchy = {
          zoneId: "",
          regionId: "",
          areaId: "",
          districtId: "",
          mandalId: "",
        };
        const targetType = normalizeTargetType(editData.target_area_type);
        const targetId = editData.target_area_id;
        // ...existing code...
        if (targetType === "zone") {
          hierarchy.zoneId = targetId;
        } else if (targetType === "region") {
          const region = regionList.find((r) => String(r.regionId) === String(targetId));
          if (region) {
            hierarchy.zoneId = region.zoneId;
            hierarchy.regionId = targetId;
          }
        } else if (targetType === "area") {
          const area = areaList.find((a) => String(a.areaId) === String(targetId));
          if (area) {
            const region = regionList.find((r) => String(r.regionId) === String(area.regionId));
            if (region) {
              hierarchy.zoneId = region.zoneId;
              hierarchy.regionId = area.regionId;
            }
            hierarchy.areaId = targetId;
          }
        } else if (targetType === "district") {
          const district = districtList.find((d) => String(d.districtId) === String(targetId));
          if (district) {
            const area = areaList.find((a) => String(a.areaId) === String(district.areaId));
            if (area) {
              const region = regionList.find((r) => String(r.regionId) === String(area.regionId));
              if (region) {
                hierarchy.zoneId = region.zoneId;
                hierarchy.regionId = area.regionId;
              }
              hierarchy.areaId = area.areaId;
            }
            hierarchy.districtId = targetId;
          }
        } else if (targetType === "mandal") {
          const mandal = mandalList.find((m) => String(m.mandalId) === String(targetId));
          if (mandal) {
            const district = districtList.find((d) => String(d.districtId) === String(mandal.districtId));
            if (district) {
              const area = areaList.find((a) => String(a.areaId) === String(district.areaId));
              if (area) {
                const region = regionList.find((r) => String(r.regionId) === String(area.regionId));
                if (region) {
                  hierarchy.zoneId = region.zoneId;
                  hierarchy.regionId = area.regionId;
                }
                hierarchy.areaId = area.areaId;
              }
              hierarchy.districtId = district.districtId;
            }
            hierarchy.mandalId = targetId;
          }
        }
        return hierarchy;
      };
      const hierarchy = populateHierarchy();
      return {
        seasonId: editData.season_id || "",
        targetLocationType: getUiTargetType(editData.target_area_type),
        vlmCount: editData.vlm_count ?? "",
        mvlmCount: editData.mvlm_count ?? "",
        farmers_attended: editData.farmers_attended ?? "",
        ...hierarchy,
        is_active: typeof editData.is_active === "boolean" ? editData.is_active : true,
      };
    } else {
      // Autofill for RM/AM
      let autofill = {
        seasonId: "",
        targetLocationType: "",
        zoneId: "",
        regionId: "",
        areaId: "",
        districtId: "",
        mandalId: "",
        vlmCount: "",
        mvlmCount: "",
        farmers_attended: "",
        is_active: true,
      };
      try {
        const userDetails = JSON.parse(localStorage.getItem("userDetails"));
        const role = (userDetails?.role || "").toUpperCase();
        const userId = userDetails?.user_id || userDetails?.id;
        if (role === "RM") {
          // Find region where inchargeUserId matches userId
          const rmRegion = regionList.find(r => String(r.inchargeUserId) === String(userId));
          const rmZone = rmRegion ? zoneList.find(z => String(z.zoneId) === String(rmRegion.zoneId)) : null;
          autofill.regionId = rmRegion ? rmRegion.regionId : "";
          autofill.zoneId = rmZone ? rmZone.zoneId : "";
        }
        if (role === "AM") {
          // Find area where inchargeUserId matches userId
          const amArea = areaList.find(a => String(a.inchargeUserId) === String(userId));
          const amRegion = amArea ? regionList.find(r => String(r.regionId) === String(amArea.regionId)) : null;
          const amZone = amRegion ? zoneList.find(z => String(z.zoneId) === String(amRegion.zoneId)) : null;
          autofill.areaId = amArea ? amArea.areaId : "";
          autofill.regionId = amRegion ? amRegion.regionId : "";
          autofill.zoneId = amZone ? amZone.zoneId : "";
        }
      } catch (e) {}
      console.log("Autofill values:", autofill);
      return autofill;
    }
  }, [editData, zoneList, regionList, areaList, districtList, mandalList]);
      

  // Build payload for create/update
  const buildPayload = (values) => {
    const selectedType = normalizeTargetType(values.targetLocationType);
    let target_area_type = values.targetLocationType;
    let target_area_id = null;
    
    if (selectedType === "zone") {
      target_area_type = "Zone";
      target_area_id = Number(values.zoneId);
    } else if (selectedType === "region") {
      target_area_type = "Region";
      target_area_id = Number(values.regionId);
    } else if (selectedType === "area") {
      target_area_type = "Area";
      target_area_id = Number(values.areaId);
    } else if (selectedType === "district") {
      target_area_type = "district";
      target_area_id = Number(values.districtId);
    } else if (selectedType === "mandal") {
      target_area_type = "mandal";
      target_area_id = Number(values.mandalId);
    }
    
    return {
      season_id: Number(values.seasonId),
      target_area_type,
      target_area_id,
      vlm_count: Number(values.vlmCount),
      mvlm_count: Number(values.mvlmCount),
      farmers_attended: Number(values.farmers_attended),
      is_active: values.is_active,
    };
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
       mt:{xs:8,sm:7,md:0}
       

      }}
    >
      <Card
        sx={{
          background: "#FFFFFF",
          borderRadius: 3,
          p:1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h4" fontWeight={600}>
            Pre season Information
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate(-1)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          onSubmit={(values, { setSubmitting }) => {
            const payload = buildPayload(values);
            if (isEditMode && id) {
              // Update mode
              dispatch(updatePreSeasonActivityAction({ ...payload, endPoint: `target/pre-season/${id}` }));
            } else {
              // Create mode
              dispatch(createPreSeasonActivityAction(payload));
            }
            setSubmitting(false);
          }}
        >
          {(formik) => {
            const { values, handleChange, setFieldValue, isSubmitting } = formik;
            const locationFields = fieldsByType[normalizeTargetType(values.targetLocationType)] || [];

            console.log("values.regionId:", values.regionId);
            
            return (
              <Form>
                <Box display="flex" justifyContent="flex-end" mb={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="is_active"
                        checked={values.is_active}
                        onChange={(e) => setFieldValue("is_active", e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Active"
                    labelPlacement="start"
                  />
                </Box>
                <Grid container spacing={3}>
                  {/* Season */}
                  <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 6, }}>
                    <Autocomplete
                      options={seasonList.map(s => ({ id: s.seasonId, label: s.name + (s.year ? ` (${s.year})` : '') }))}
                      value={seasonList.map(s => ({ id: s.seasonId, label: s.name + (s.year ? ` (${s.year})` : '') })).find(s => String(s.id) === String(values.seasonId)) || null}
                      onChange={(e, val) => setFieldValue('seasonId', val ? val.id : '')}
                      getOptionLabel={option => option?.label || ''}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      clearOnEscape
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          label="Season"
                          name="seasonId"
                          placeholder="Search season..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: '#FAFAFA',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                    {/* Target Area Type */}
                  <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 6, }}>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      label="Target Area Type"
                      name="targetLocationType"
                      value={values.targetLocationType}
                      onChange={(e) => {
                        const newType = normalizeTargetType(e.target.value);
                        handleChange(e);
                        
                        // Only clear fields that won't be visible for the new type
                        const newFields = fieldsByType[newType] || [];           
                        
                        if (!newFields.includes("zone")) setFieldValue("zoneId", "");
                        if (!newFields.includes("region")) setFieldValue("regionId", "");
                        if (!newFields.includes("area")) setFieldValue("areaId", "");
                        if (!newFields.includes("district")) setFieldValue("districtId", "");
                        if (!newFields.includes("mandal")) setFieldValue("mandalId", "");
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: '#FAFAFA',
                        },
                      }}
                    >
                      <MenuItem value="">Select Type</MenuItem>
                      <MenuItem value="Zone">Zone</MenuItem>
                      <MenuItem value="Region">Region</MenuItem>
                      <MenuItem value="Area">Area</MenuItem>
                      <MenuItem value="District">District</MenuItem>
                      <MenuItem value="Mandal">Mandal</MenuItem>
                    </TextField>
                  </Grid>
                  </Grid>
                  
                  <Grid container spacing={3} mt={2}>
                  {/* Zone Autocomplete */}
                  {locationFields.includes("zone") && (
                    <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 3, md: 3 }}>
                      <Autocomplete
                        options={zoneList.map(z => ({ id: z.zoneId, label: z.zoneName }))}
                        loading={zonesLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#FAFAFA',
                          },
                        }}
                        value={zoneList.map(z => ({ id: z.zoneId, label: z.zoneName })).find(z => String(z.id) === String(values.zoneId)) || null}
                        onChange={(e, val) => {
                          setFieldValue("zoneId", val ? val.id : "");
                          setFieldValue("regionId", "");
                          setFieldValue("areaId", "");
                          setFieldValue("districtId", "");
                          setFieldValue("mandalId", "");
                        }}
                        getOptionLabel={option => option?.label || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        clearOnEscape
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            label="Zone"
                            name="zoneId"
                            onBlur={() => formik.setFieldTouched("zoneId", true)}
                            error={formik.touched.zoneId && Boolean(formik.errors.zoneId)}
                            helperText={formik.touched.zoneId && formik.errors.zoneId}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {/* Region Autocomplete */}
                  {locationFields.includes("region") && (
                    <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 3, md: 3 }}>
                      <Autocomplete
                        options={regionList.filter(r => String(r.zoneId) === String(values.zoneId)).map(r => ({ id: r.regionId, label: r.regionName }))}
                        loading={regionsLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#FAFAFA',
                          },
                        }}
                        value={regionList.filter(r => String(r.zoneId) === String(values.zoneId)).map(r => ({ id: r.regionId, label: r.regionName })).find(r => String(r.id) === String(values.regionId)) || null}
                        onChange={(e, val) => {
                          setFieldValue("regionId", val ? val.id : "");
                          setFieldValue("areaId", "");
                          setFieldValue("districtId", "");
                          setFieldValue("mandalId", "");
                        }}
                        getOptionLabel={option => option?.label || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        clearOnEscape
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            label="Region"
                            name="regionId"
                            onBlur={() => formik.setFieldTouched("regionId", true)}
                            error={formik.touched.regionId && Boolean(formik.errors.regionId)}
                            helperText={formik.touched.regionId && formik.errors.regionId}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {/* Area Autocomplete */}
                  {locationFields.includes("area") && (
                    <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 3, md: 3 }}>
                      <Autocomplete
                        options={areaList.filter(a => String(a.regionId) === String(values.regionId)).map(a => ({ id: a.areaId, label: a.areaName }))}
                        loading={areasLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#FAFAFA',
                          },
                        }}
                        value={areaList.filter(a => String(a.regionId) === String(values.regionId)).map(a => ({ id: a.areaId, label: a.areaName })).find(a => String(a.id) === String(values.areaId)) || null}
                        onChange={(e, val) => {
                          setFieldValue("areaId", val ? val.id : "");
                          setFieldValue("districtId", "");
                          setFieldValue("mandalId", "");
                        }}
                        getOptionLabel={option => option?.label || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        clearOnEscape
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            label="Area"
                            name="areaId"
                            onBlur={() => formik.setFieldTouched("areaId", true)}
                            error={formik.touched.areaId && Boolean(formik.errors.areaId)}
                            helperText={formik.touched.areaId && formik.errors.areaId}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {/* District Autocomplete */}
                  {locationFields.includes("district") && (
                    <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 3, md: 3 }}>
                      <Autocomplete
                        options={districtList.filter(d => String(d.areaId) === String(values.areaId)).map(d => ({ id: d.districtId, label: d.name }))}
                        loading={districtsLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#FAFAFA',
                          },
                        }}
                        value={districtList.filter(d => String(d.areaId) === String(values.areaId)).map(d => ({ id: d.districtId, label: d.name })).find(d => String(d.id) === String(values.districtId)) || null}
                        onChange={(e, val) => {
                          setFieldValue("districtId", val ? val.id : "");
                          setFieldValue("mandalId", "");
                        }}
                        getOptionLabel={option => option?.label || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        clearOnEscape
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            label="District"
                            name="districtId"
                            onBlur={() => formik.setFieldTouched("districtId", true)}
                            error={formik.touched.districtId && Boolean(formik.errors.districtId)}
                            helperText={formik.touched.districtId && formik.errors.districtId}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {/* Mandal Autocomplete */}
                  {locationFields.includes("mandal") && (
                    <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 3, md: 3 }}>
                      <Autocomplete
                        options={mandalList.filter(m => String(m.districtId) === String(values.districtId)).map(m => ({ id: m.mandalId, label: m.name }))}
                        loading={mandalsLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#FAFAFA',
                          },
                        }}
                        value={mandalList.filter(m => String(m.districtId) === String(values.districtId)).map(m => ({ id: m.mandalId, label: m.name })).find(m => String(m.id) === String(values.mandalId)) || null}
                        onChange={(e, val) => {
                          setFieldValue("mandalId", val ? val.id : "");
                        }}
                        getOptionLabel={option => option?.label || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        clearOnEscape
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            label="Mandal"
                            name="mandalId"
                            onBlur={() => formik.setFieldTouched("mandalId", true)}
                            error={formik.touched.mandalId && Boolean(formik.errors.mandalId)}
                            helperText={formik.touched.mandalId && formik.errors.mandalId}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  </Grid>

                 <Grid container spacing={3} mt={2}>
                  {/* VLM Count */}
                  <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 6,  }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="VLM Count"
                      name="vlmCount"
                      placeholder="Enter VLM count"
                      value={values.vlmCount}
                      onChange={handleChange}
                      slotProps={{
                        input: {
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          style: { MozAppearance: 'textfield' }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: '#FAFAFA',
                        },
                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                      }}
                    />
                  </Grid>
                  {/* MVLM Count */}
                  <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 6, }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="MVLM Count"
                      name="mvlmCount"
                      placeholder="Enter MVLM count"
                      value={values.mvlmCount}
                      onChange={handleChange}
                      slotProps={{
                        input: {
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          style: { MozAppearance: 'textfield' }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: '#FAFAFA',
                        },
                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                      }}
                    />
                  </Grid>
                  </Grid>

                  {/* Farmers Attended */}
                  {/* <Grid item sx={{ flexGrow: 1 }} size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      size="small"
                      type="number"
                      fullWidth
                      label="Farmers Attended"
                      name="farmers_attended"
                      placeholder="Enter Farmers Attended"
                      value={values.farmers_attended}
                      onChange={handleChange}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: '#FAFAFA',
                        },
                      }}
                    />
                  </Grid> */}
                {/* </Grid> */}
                {/* Actions */}
                <Box
                  mt={5}
                >
                  <Grid container justifyContent={{ xs: 'stretch', sm: 'flex-end' }}>
                    <Grid item xs={12} sm={8} md={6} lg={4}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Button
                            variant="outlined"
                            sx={{
                              width: '100%',
                              borderRadius: 3,
                              textTransform: "none",
                            }}
                            onClick={() => navigate(-1)}
                          >
                            Cancel
                          </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Button
                            type="submit"
                            variant="contained"
                            sx={{
                              width: '100%',
                              borderRadius: 3,
                              background: "#4F46E5",
                              textTransform: "none",
                            }}
                            disabled={isSubmitting}
                          >
                            {isEditMode ? "Update" : "Submit"}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Card>
    </Box>
  );
}

